/**
 * Mail setup wizard — digitaladmin's Microsoft auto-configure, ported (their
 * Step 74 / our Steps 10-11 family). The admin signs in to Microsoft ONCE
 * (device code); THE APP then creates or links the registration, mints the
 * secret and grants Mail.Send consent itself — nobody clicks through Entra
 * admin. Per-step pass/fail checklist so a partial failure is diagnosable.
 * Nothing activates until a real test email has gone through.
 */
import { useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { CheckCircle2, ChevronLeft, Copy, ExternalLink, Mail, Send, Server, XCircle } from 'lucide-react';
import { Modal } from '../../components/ui/modal';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { apiSend, apiFetch } from '../../lib/api';
import { useDirty } from '../../lib/dirty';
import { useAuth } from '../../lib/AuthContext';
import { qk } from '../../lib/queryClient';
import { cn } from '../../lib/utils';

type Step = 'transport' | 'signin' | 'app' | 'mailbox' | 'provision' | 'smtp' | 'test' | 'done';
interface StepResult {
  pass: boolean;
  label: string;
  error?: string;
}

const MASKED = '••••••••';

export function MailSetupWizard({
  scope,
  hasStoredSecret,
  signInAppClientId,
  credentialsReady,
  open,
  onClose,
}: {
  scope: 'org' | 'global';
  hasStoredSecret: boolean;
  /**
   * Step 171 — this organisation already has working Microsoft credentials,
   * carried over from the sign-in setup, so there is nothing to provision and
   * nobody needs to sign in to Microsoft again. All that is left is naming the
   * mailbox and proving a message can leave the building.
   */
  credentialsReady?: boolean;
  /** Step 171 — the sign-in registration's client ID, when there is one, so
   *  "reuse the application I already made" is a button rather than a trip to
   *  the Entra portal to copy a GUID. */
  signInAppClientId?: string | null;
  open: boolean;
  onClose: (finished: boolean) => void;
}) {
  const { me } = useAuth();
  const queryClient = useQueryClient();
  const [step, setStep] = useState<Step>('transport');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Device-code session
  const [session, setSession] = useState<{
    sessionId: string;
    userCode: string;
    verificationUri: string;
  } | null>(null);
  const [tenantName, setTenantName] = useState('');
  const pollTimer = useRef<ReturnType<typeof setInterval> | null>(null);

  // App choice
  // URL state on purpose: a step inside a setup wizard. The wizard is an
  // overlay over the settings page — restoring step 3 from a link would drop
  // somebody mid-provisioning with none of the earlier answers.
  const [mode, setMode] = useState<'create' | 'link'>('create');
  const [existingAppId, setExistingAppId] = useState('');
  const [lookedUp, setLookedUp] = useState<{ displayName: string } | null>(null);
  const [secretChoice, setSecretChoice] = useState<'generate' | 'manual'>('generate');
  const [manualSecret, setManualSecret] = useState('');
  /** Step 171 — Kai: "I can't choose the name for my application." Empty means
   *  "use the default the server would have picked". */
  const [appDisplayName, setAppDisplayName] = useState('');
  /** Step 171 — read the staff directory and groups as well as mail. On by
   *  default: without it nobody can be provisioned and every people-picker is
   *  empty until somebody emails in. */
  const [directoryAccess, setDirectoryAccess] = useState(true);

  /**
   * A part-completed provisioning wizard is a draft: a tenant name, an app
   * choice and possibly a hand-entered client secret. Losing it silently means
   * redoing the Entra side, so the router-level guard must ask first.
   */
  useDirty(!!tenantName.trim() || !!existingAppId.trim() || !!manualSecret.trim());

  // Mailbox + provisioning outcome
  const [senderMailbox, setSenderMailbox] = useState('');
  const [fromName, setFromName] = useState('');
  const [provisionSteps, setProvisionSteps] = useState<StepResult[] | null>(null);
  const [provisionPass, setProvisionPass] = useState(false);

  // SMTP branch
  const [smtp, setSmtp] = useState({ host: '', port: '', user: '', pass: '', fromAddress: '', fromName: '' });

  // Test
  const [testTo, setTestTo] = useState(me?.user.email ?? '');
  const [testResult, setTestResult] = useState<'ok' | string | null>(null);

  useEffect(
    () => () => {
      if (pollTimer.current) clearInterval(pollTimer.current);
    },
    [],
  );

  function close(finished: boolean) {
    if (pollTimer.current) clearInterval(pollTimer.current);
    if (session && !finished) void apiSend(`/api/admin/mail-settings/ms/${session.sessionId}/cancel`, 'POST');
    onClose(finished);
  }

  async function startSignin() {
    setBusy(true);
    setError(null);
    const res = await apiSend<{
      sessionId: string;
      userCode: string;
      verificationUri: string;
      interval: number;
    }>('/api/admin/mail-settings/ms/start', 'POST', { scope });
    setBusy(false);
    if (!res.ok) return setError(res.error);
    setSession(res.body!);
    setStep('signin');
    const every = Math.max(res.body!.interval ?? 5, 3) * 1000;
    pollTimer.current = setInterval(async () => {
      const poll = await apiFetch<{ status: string; plan?: { tenantName: string }; message?: string }>(
        `/api/admin/mail-settings/ms/${res.body!.sessionId}/poll`,
      ).catch(() => null);
      if (!poll) return;
      if (poll.status === 'ready') {
        if (pollTimer.current) clearInterval(pollTimer.current);
        setTenantName(poll.plan?.tenantName ?? '');
        setStep('app');
      } else if (poll.status === 'failed') {
        if (pollTimer.current) clearInterval(pollTimer.current);
        setError(poll.message ?? 'Sign-in failed');
        setStep('transport');
      }
    }, every);
  }

  async function lookup() {
    setBusy(true);
    setError(null);
    setLookedUp(null);
    try {
      const app = await apiFetch<{ displayName: string }>(
        `/api/admin/mail-settings/ms/${session!.sessionId}/lookup?appId=${encodeURIComponent(existingAppId.trim())}`,
      );
      setLookedUp(app);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  async function provision() {
    setBusy(true);
    setError(null);
    setStep('provision');
    const res = await apiSend<{ pass: boolean; steps: StepResult[] }>(
      `/api/admin/mail-settings/ms/${session!.sessionId}/provision`,
      'POST',
      {
        scope,
        mode,
        existingAppId: mode === 'link' ? existingAppId.trim() : undefined,
        appDisplayName: mode === 'create' && appDisplayName.trim() ? appDisplayName.trim() : undefined,
        directoryAccess,
        generateNewSecret: secretChoice === 'generate',
        manualSecret: secretChoice === 'manual' ? manualSecret : undefined,
        senderMailbox: senderMailbox.trim(),
        fromName: fromName.trim() || undefined,
      },
    );
    setBusy(false);
    if (!res.ok) {
      setError(res.error);
      setProvisionSteps([]);
      return;
    }
    setProvisionSteps(res.body!.steps);
    setProvisionPass(res.body!.pass);
  }

  async function saveSmtpInactive(): Promise<boolean> {
    setBusy(true);
    setError(null);
    const res = await apiSend('/api/admin/mail-settings', 'PUT', {
      scope,
      mode: 'smtp',
      isActive: false,
      fromAddress: smtp.fromAddress || null,
      fromName: smtp.fromName || null,
      smtpHost: smtp.host || null,
      smtpPort: smtp.port ? Number(smtp.port) : null,
      smtpUser: smtp.user || null,
      smtpPass: smtp.pass || (hasStoredSecret ? MASKED : null),
    });
    setBusy(false);
    if (!res.ok) setError(res.error);
    return res.ok;
  }

  async function runTest() {
    setBusy(true);
    setTestResult(null);
    const res = await apiSend<{ ok: boolean; error: string | null }>(
      '/api/admin/mail-settings/test',
      'POST',
      {
        scope,
        to: testTo,
      },
    );
    setBusy(false);
    if (!res.ok) setTestResult(res.error ?? 'test failed');
    else setTestResult(res.body!.ok ? 'ok' : (res.body!.error ?? 'test failed'));
  }

  async function activate() {
    setBusy(true);
    const res = await apiSend('/api/admin/mail-settings/activate', 'POST', { scope, isActive: true });
    setBusy(false);
    if (!res.ok) return setError(res.error);
    void queryClient.invalidateQueries({ queryKey: qk.mailSettings });
    close(true);
  }

  const STEPS: Step[] = ['transport', 'signin', 'app', 'mailbox', 'provision', 'test', 'done'];
  const LABELS = ['Transport', 'Sign in', 'App', 'Mailbox', 'Provision', 'Test', 'Turn on'];
  const stepIndex = step === 'smtp' ? 1 : STEPS.indexOf(step);

  return (
    <Modal
      open={open}
      onClose={() => close(false)}
      title={`Mail setup — ${scope === 'org' ? 'this organisation (override)' : 'global (the default)'}`}
      wide
    >
      <div className="mb-4 flex gap-1">
        {LABELS.map((label, i) => (
          <div key={label} className="flex-1">
            <div className={cn('h-1 rounded-full', i <= stepIndex ? 'bg-brand' : 'bg-muted')} />
            <p
              className={cn(
                'mt-1 text-[10px]',
                i === stepIndex ? 'font-medium text-foreground' : 'text-muted-foreground',
              )}
            >
              {label}
            </p>
          </div>
        ))}
      </div>

      {step === 'transport' && (
        <div className="space-y-3">
          {credentialsReady && (
            <button
              className="flex w-full items-start gap-3 rounded-lg border border-brand p-3 text-left ring-1 ring-brand transition-colors hover:bg-accent/50"
              onClick={() => setStep('mailbox')}
            >
              <Mail className="mt-0.5 h-5 w-5 text-brand" />
              <span>
                <span className="block text-sm font-medium">
                  Microsoft 365 — already connected, just pick the mailbox
                </span>
                <span className="block text-xs text-muted-foreground">
                  The sign-in setup gave this service desk permission to send and read mail, so there is
                  nothing else to authorise. Name the mailbox and send a test.
                </span>
              </span>
            </button>
          )}
          <button
            className="flex w-full items-start gap-3 rounded-lg border p-3 text-left transition-colors hover:bg-accent/50"
            onClick={startSignin}
            disabled={busy}
          >
            <Mail className="mt-0.5 h-5 w-5 shrink-0 text-brand" />
            <span>
              <span className="block text-sm font-medium">
                {credentialsReady
                  ? 'Microsoft 365 — set up a separate application'
                  : 'Microsoft 365 — set it up for me'}
              </span>
              <span className="block text-xs text-muted-foreground">
                Sign in to Microsoft once; the app then creates (or reuses) the app registration, generates
                the secret and grants the send and read permissions itself. Nothing to click through in Entra
                admin.
              </span>
            </span>
          </button>
          <button
            className="flex w-full items-start gap-3 rounded-lg border p-3 text-left transition-colors hover:bg-accent/50"
            onClick={() => setStep('smtp')}
          >
            <Server className="mt-0.5 h-5 w-5 shrink-0 text-brand" />
            <span>
              <span className="block text-sm font-medium">SMTP</span>
              <span className="block text-xs text-muted-foreground">
                Works with anything that hands you SMTP credentials.
              </span>
            </span>
          </button>
          {error && <p className="text-xs text-destructive">{error}</p>}
        </div>
      )}

      {step === 'signin' && session && (
        <div className="space-y-3 text-center">
          <p className="text-sm text-muted-foreground">
            Open the Microsoft page and enter this code. You need to sign in as someone who can create app
            registrations (Application Administrator or Global Administrator).
          </p>
          <div className="mx-auto flex w-fit items-center gap-2 rounded-lg border bg-muted/40 px-4 py-2">
            <span className="font-mono text-2xl font-bold tracking-widest">{session.userCode}</span>
            <Button
              variant="ghost"
              size="icon"
              aria-label="Copy code"
              onClick={() => void navigator.clipboard.writeText(session.userCode).catch(() => {})}
            >
              <Copy className="h-4 w-4" />
            </Button>
          </div>
          <Button onClick={() => window.open(session.verificationUri, '_blank', 'noopener')}>
            <ExternalLink /> Open microsoft.com/devicelogin
          </Button>
          <p className="text-xs text-muted-foreground">Waiting for you to finish signing in…</p>
        </div>
      )}

      {step === 'app' && (
        <div className="space-y-3">
          {tenantName && (
            <p className="text-xs text-muted-foreground">
              Signed in to <span className="font-medium text-foreground">{tenantName}</span>.
            </p>
          )}
          <div className="space-y-2">
            <button
              className={cn(
                'w-full rounded-lg border p-3 text-left text-sm transition-colors hover:bg-accent/50',
                mode === 'create' && 'border-brand ring-1 ring-brand',
              )}
              onClick={() => setMode('create')}
            >
              <span className="font-medium">Create a new app registration</span>
              <span className="block text-xs text-muted-foreground">
                Dedicated to mail. You choose what it is called.
              </span>
            </button>
            <button
              className={cn(
                'w-full rounded-lg border p-3 text-left text-sm transition-colors hover:bg-accent/50',
                mode === 'link' && 'border-brand ring-1 ring-brand',
              )}
              onClick={() => setMode('link')}
            >
              <span className="font-medium">
                {signInAppClientId ? 'Reuse the sign-in application' : 'Use an existing app registration'}
              </span>
              <span className="block text-xs text-muted-foreground">
                {signInAppClientId
                  ? 'The one this service desk already uses for Microsoft sign-in. The mail permissions are added to it; nothing is taken away, so sign-in keeps working.'
                  : 'Paste the Application (client) ID from the Entra portal. Set up Microsoft sign-in first and this offers that application, so you do not have to go and find it.'}
              </span>
            </button>
          </div>
          {mode === 'create' && (
            <div className="space-y-1.5">
              <label htmlFor="mail-app-name" className="text-sm font-medium">
                What should it be called in Microsoft?
              </label>
              <Input
                id="mail-app-name"
                placeholder="Service Desk Mail"
                value={appDisplayName}
                onChange={(e) => setAppDisplayName(e.target.value)}
              />
              <p className="text-xs text-muted-foreground">
                This is the name people will see in the Entra portal. Leave it empty and it is named after
                your service desk.
              </p>
            </div>
          )}
          <label className="flex items-start gap-2 rounded-lg border p-3 text-sm">
            <input
              type="checkbox"
              className="mt-0.5"
              checked={directoryAccess}
              onChange={(e) => setDirectoryAccess(e.target.checked)}
            />
            <span>
              <span className="font-medium">Also read the staff directory and groups</span>
              <span className="block text-xs text-muted-foreground">
                Read-only: users, groups, and this application's own role assignments. It is what lets the
                service desk know who works here before they ever contact it — people-pickers find them,
                automations can route on department and location, and either an Entra security group or an
                assignment on the enterprise application can grant a role. Without it those sit empty and the
                sync fails with a permissions error. Untick only if this desk sends mail and nothing else.
              </span>
            </span>
          </label>
          {mode === 'link' && (
            <div className="space-y-2">
              {signInAppClientId && existingAppId.trim() !== signInAppClientId && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setExistingAppId(signInAppClientId);
                    setLookedUp(null);
                  }}
                >
                  Use the sign-in application
                </Button>
              )}
              <div className="flex gap-2">
                <Input
                  placeholder="Application (client) ID"
                  value={existingAppId}
                  onChange={(e) => {
                    setExistingAppId(e.target.value);
                    setLookedUp(null);
                  }}
                />
                <Button variant="outline" onClick={lookup} disabled={busy || !existingAppId.trim()}>
                  Look up
                </Button>
              </div>
              {lookedUp && (
                <p className="text-xs text-emerald-600 dark:text-emerald-400">
                  Found: <span className="font-medium">{lookedUp.displayName}</span>
                </p>
              )}
              <div className="flex gap-1">
                {(['generate', 'manual'] as const).map((c) => (
                  <button
                    key={c}
                    onClick={() => setSecretChoice(c)}
                    className={cn(
                      'rounded-md px-2.5 py-1 text-xs font-medium focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                      secretChoice === c
                        ? 'bg-primary text-primary-foreground'
                        : 'text-muted-foreground hover:bg-accent',
                    )}
                  >
                    {c === 'generate' ? 'Generate a new secret' : 'I have a secret already'}
                  </button>
                ))}
              </div>
              {secretChoice === 'manual' && (
                <Input
                  type="password"
                  placeholder="Existing client secret"
                  value={manualSecret}
                  onChange={(e) => setManualSecret(e.target.value)}
                />
              )}
            </div>
          )}
          {error && <p className="text-xs text-destructive">{error}</p>}
          <div className="flex justify-end pt-1">
            <Button
              disabled={mode === 'link' && (!lookedUp || (secretChoice === 'manual' && !manualSecret))}
              onClick={() => setStep('mailbox')}
            >
              Next
            </Button>
          </div>
        </div>
      )}

      {step === 'mailbox' && (
        <div className="space-y-3">
          <div>
            <label className="mb-1 block text-xs font-medium text-muted-foreground">
              Shared mailbox to send as
            </label>
            <Input
              type="email"
              placeholder="support@yourdomain.org.uk"
              value={senderMailbox}
              onChange={(e) => setSenderMailbox(e.target.value)}
            />
            <p className="mt-1 text-[11px] text-muted-foreground">
              Tip: scope the app to just this mailbox afterwards with an Exchange ApplicationAccessPolicy —
              the settings page shows the one-line command.
            </p>
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-muted-foreground">From name</label>
            <Input placeholder="Support" value={fromName} onChange={(e) => setFromName(e.target.value)} />
          </div>
          <div className="flex justify-between pt-1">
            <Button variant="ghost" onClick={() => setStep('app')}>
              <ChevronLeft /> Back
            </Button>
            <Button disabled={busy || !senderMailbox.trim()} onClick={provision}>
              Set it up
            </Button>
          </div>
        </div>
      )}

      {step === 'provision' && (
        <div className="space-y-3">
          {!provisionSteps && <p className="text-sm text-muted-foreground">Talking to Microsoft…</p>}
          {provisionSteps && (
            <ul className="space-y-1.5">
              {provisionSteps.map((s) => (
                <li key={s.label} className="flex items-start gap-2 text-sm">
                  {s.pass ? (
                    <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-emerald-500" />
                  ) : (
                    <XCircle className="mt-0.5 h-4 w-4 shrink-0 text-destructive" />
                  )}
                  <span>
                    {s.label}
                    {s.error && (
                      <span className="block break-all font-mono text-[11px] text-destructive/90">
                        {s.error}
                      </span>
                    )}
                  </span>
                </li>
              ))}
            </ul>
          )}
          {error && <p className="text-xs text-destructive">{error}</p>}
          {provisionSteps && (
            <div className="flex justify-between pt-1">
              <Button variant="ghost" onClick={() => setStep('mailbox')}>
                <ChevronLeft /> Back
              </Button>
              <Button disabled={!provisionPass} onClick={() => setStep('test')}>
                {provisionPass ? 'Next — test it' : 'Fix the failed step to continue'}
              </Button>
            </div>
          )}
        </div>
      )}

      {step === 'smtp' && (
        <div className="space-y-3">
          <div className="grid grid-cols-3 gap-3">
            <div className="col-span-2">
              <label className="mb-1 block text-xs font-medium text-muted-foreground">SMTP host</label>
              <Input value={smtp.host} onChange={(e) => setSmtp({ ...smtp, host: e.target.value })} />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-muted-foreground">Port</label>
              <Input
                type="number"
                placeholder="587"
                value={smtp.port}
                onChange={(e) => setSmtp({ ...smtp, port: e.target.value })}
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="mb-1 block text-xs font-medium text-muted-foreground">Username</label>
              <Input value={smtp.user} onChange={(e) => setSmtp({ ...smtp, user: e.target.value })} />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-muted-foreground">
                Password{hasStoredSecret ? ' (blank keeps the stored one)' : ''}
              </label>
              <Input
                type="password"
                value={smtp.pass}
                onChange={(e) => setSmtp({ ...smtp, pass: e.target.value })}
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="mb-1 block text-xs font-medium text-muted-foreground">From address</label>
              <Input
                type="email"
                value={smtp.fromAddress}
                onChange={(e) => setSmtp({ ...smtp, fromAddress: e.target.value })}
              />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-muted-foreground">From name</label>
              <Input value={smtp.fromName} onChange={(e) => setSmtp({ ...smtp, fromName: e.target.value })} />
            </div>
          </div>
          {error && <p className="text-xs text-destructive">{error}</p>}
          <div className="flex justify-between pt-1">
            <Button variant="ghost" onClick={() => setStep('transport')}>
              <ChevronLeft /> Back
            </Button>
            <Button
              disabled={busy || !smtp.fromAddress.trim() || !smtp.host.trim()}
              onClick={async () => {
                if (await saveSmtpInactive()) setStep('test');
              }}
            >
              {busy ? 'Saving…' : 'Save & continue'}
            </Button>
          </div>
        </div>
      )}

      {step === 'test' && (
        <div className="space-y-3">
          <p className="text-sm text-muted-foreground">
            Everything is saved but <b>not switched on yet</b>. Prove it — send yourself a test email.
          </p>
          <div className="flex gap-2">
            <Input type="email" value={testTo} onChange={(e) => setTestTo(e.target.value)} />
            <Button onClick={runTest} disabled={busy || !testTo.trim()}>
              <Send /> {busy ? 'Sending…' : 'Send test'}
            </Button>
          </div>
          {testResult === 'ok' && (
            <div className="flex items-start gap-2 rounded-md border border-emerald-500/30 bg-emerald-500/10 p-3">
              <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-emerald-500" />
              <p className="text-sm">Sent. Check the inbox, then turn it on.</p>
            </div>
          )}
          {testResult && testResult !== 'ok' && (
            <div className="rounded-md border border-destructive/30 bg-destructive/10 p-3">
              <p className="text-xs font-medium text-destructive">The transport said:</p>
              <p className="mt-1 break-all font-mono text-[11px] text-destructive/90">{testResult}</p>
            </div>
          )}
          <div className="flex justify-end pt-1">
            <Button disabled={testResult !== 'ok'} onClick={() => setStep('done')}>
              Next
            </Button>
          </div>
        </div>
      )}

      {step === 'done' && (
        <div className="space-y-3">
          <p className="text-sm">
            The test went through. Turning this on makes it the{' '}
            {scope === 'org' ? 'organisation’s override' : 'global default'} transport — magic links,
            notifications, everything.
          </p>
          {error && <p className="text-xs text-destructive">{error}</p>}
          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={() => close(false)}>
              Not yet
            </Button>
            <Button onClick={activate} disabled={busy}>
              {busy ? 'Turning on…' : 'Turn it on'}
            </Button>
          </div>
        </div>
      )}
    </Modal>
  );
}
